import type { PaletteMode } from '@mui/material';
import { alpha, createTheme } from '@mui/material/styles';

export const getMuiTheme = (mode: PaletteMode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#5a4bfc',
        light: '#8376ff',
        dark: '#2f2bb5',
      },
      secondary: {
        main: '#00b894',
        light: '#33c7a9',
        dark: '#008367',
      },
      background: {
        default: mode === 'dark' ? '#070a0f' : '#f4f6fb',
        paper: mode === 'dark' ? '#0f172a' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#e5e7eb' : '#12131a',
        secondary: mode === 'dark' ? '#94a3b8' : '#5e6473',
      },
      divider: mode === 'dark' ? alpha('#94a3b8', 0.18) : 'rgba(18, 19, 26, 0.08)',
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', 'Segoe UI', sans-serif",
      h1: {
        fontSize: '2.75rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '-0.015em',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: '1.1rem',
        fontWeight: 600,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.94rem',
        lineHeight: 1.55,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
      },
    },
    shape: {
      borderRadius: 18,
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: '1.4rem',
            paddingBlock: '0.65rem',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 24,
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 24,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
              mode === 'dark' ? '0 20px 55px rgba(0, 0, 0, 0.55)' : '0 18px 40px rgba(17, 24, 39, 0.08)',
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.background.paper, mode === 'dark' ? 0.82 : 0.9),
            color: theme.palette.text.primary,
            boxShadow: mode === 'dark' ? '0 14px 45px rgba(0, 0, 0, 0.45)' : '0 8px 24px rgba(15, 23, 42, 0.08)',
            borderBottom: `1px solid ${theme.palette.divider}`,
            backdropFilter: 'blur(16px)',
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            '&.Mui-selected': {
              backgroundColor: 'rgba(90, 75, 252, 0.14)',
              color: '#5a4bfc',
              '&:hover': {
                backgroundColor: 'rgba(90, 75, 252, 0.22)',
              },
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background:
              mode === 'dark'
                ? `radial-gradient(circle at top, ${alpha('#5a4bfc', 0.12)}, transparent 55%),
                   radial-gradient(circle at bottom, ${alpha('#00b894', 0.1)}, transparent 45%),
                   #070a0f`
                : `radial-gradient(circle at top, ${alpha('#5a4bfc', 0.08)}, transparent 55%),
                   radial-gradient(circle at bottom, ${alpha('#00b894', 0.08)}, transparent 45%),
                   #f5f7fb`,
          },
        },
      },
    },
  });

export default getMuiTheme('light');
